import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Upload, Trash2, Check, Loader2 } from "lucide-react";

const FIELDS = [
  { key: "company_name", label: "Firmenname", type: "text" },
  { key: "tagline", label: "Slogan", type: "text" },
  { key: "description", label: "Beschreibung", type: "textarea" },
  { key: "phone", label: "Telefon", type: "text" },
  { key: "email", label: "E-Mail", type: "email" },
  { key: "address", label: "Adresse", type: "text" },
  { key: "business_hours", label: "Öffnungszeiten", type: "text" },
  { key: "years_experience", label: "Jahre Erfahrung", type: "number" },
  { key: "projects_completed", label: "Anzahl Projekte", type: "number" },
  { key: "stat4_value", label: "4. Statistik – Wert (z.B. „100%\u201C, „Top 3\u201C, „A+\u201C)", type: "text" },
  { key: "stat4_label", label: "4. Statistik – Beschriftung (z.B. „Meisterbetrieb\u201C, „Garantie\u201C)", type: "text" },
  { key: "google_reviews_count", label: "Google-Bewertungen Gesamtanzahl (z.B. 18) – leer = nur importierte zählen", type: "number" },
  { key: "google_rating", label: "Bewertungs-Durchschnitt anzeigen (z.B. 5.0) – leer = automatisch berechnen", type: "text" },
];

const DESIGNS = [
  {
    key: "classic",
    name: "Editorial · Premium",
    description: "Eleganter Magazin-Stil mit asymmetrischem Layout. Cormorant-Serif, warme Sandtöne, Terrakotta-Akzent. Hero mit Bild rechts, ruhige redaktionelle Anordnung.",
    swatches: ["#F9F8F6", "#EFECE6", "#C25E49", "#7A8B76", "#1A1A18"],
    fontPreview: "font-display",
    fontSample: "Aa",
    layoutTags: ["Asymmetrisch", "Editorial", "Premium", "Serif"],
  },
  {
    key: "dark",
    name: "Bold Studio · Dramatisch",
    description: "Fettes Vollbild-Foto-Hero, Mega-Headline in Archivo Black, 3-spaltige Bildkachel-Galerie, schwarzer Hintergrund mit Messing-Akzent, Partner-Leiste. Modern und dramatisch.",
    swatches: ["#0A0A0A", "#161616", "#D4A949", "#F4F0E8", "#5A554A"],
    fontPreview: "[font-family:'Archivo_Black',sans-serif]",
    fontSample: "Aa",
    layoutTags: ["Fotostark", "Bildkacheln", "Dunkel", "Bold Sans"],
  },
  {
    key: "studio",
    name: "Werkstatt · Klassisch",
    description: "Kontaktleiste oben, klassischer Header mit Inhaber-Portrait, Leistungen als abwechselnde Bild-Text-Reihen, Vertrauenspunkte, Projekt-Karten, Bewertungen. Traditioneller Mittelstands-Stil.",
    swatches: ["#FAFAF7", "#0B5394", "#E8A33D", "#073763", "#1F2937"],
    fontPreview: "[font-family:'Bitter',serif]",
    fontSample: "Aa",
    layoutTags: ["Klassisch", "Abwechselnd", "Vertrauensvoll", "Serif"],
  },
];

export default function SiteInfoManager() {
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/site-info");
    setInfo(data);
  };
  useEffect(() => {
    const init = async () => {
      const { data } = await api.get("/site-info");
      setInfo(data);
    };
    init();
  }, []);

  const update = (key) => (e) => {
    const val = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setInfo({ ...info, [key]: val });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...info, logo_path: up.path };
      const payload = { ...updated }; delete payload.id; delete payload._id;
      await api.put("/admin/site-info", payload);
      setInfo(updated);
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo-Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...info, hero_image_path: up.path };
      const payload = { ...updated }; delete payload.id; delete payload._id;
      await api.put("/admin/site-info", payload);
      setInfo(updated);
      toast.success("Hero-Bild hochgeladen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleHeroRemove = async () => {
    if (!window.confirm("Hero-Bild entfernen?")) return;
    const updated = { ...info, hero_image_path: "" };
    const payload = { ...updated }; delete payload.id; delete payload._id;
    await api.put("/admin/site-info", payload);
    setInfo(updated);
    toast.success("Hero-Bild entfernt");
  };

  const handleLogoRemove = async () => {
    if (!window.confirm("Logo entfernen?")) return;
    const updated = { ...info, logo_path: "" };
    const payload = { ...updated }; delete payload.id; delete payload._id;
    await api.put("/admin/site-info", payload);
    setInfo(updated);
    toast.success("Logo entfernt");
  };

  const selectDesign = async (designKey) => {
    const updated = { ...info, design: designKey };
    const payload = { ...updated }; delete payload.id; delete payload._id;
    await api.put("/admin/site-info", payload);
    setInfo(updated);
    toast.success(`Design "${DESIGNS.find(d => d.key === designKey).name}" aktiviert`);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...info };
      delete payload.id;
      delete payload._id;
      await api.put("/admin/site-info", payload);
      toast.success("Gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  if (!info) return <p className="text-stone-600">Laden…</p>;

  return (
    <div data-testid="site-info-manager" className="space-y-8">
      {/* LOGO */}
      <div className="bg-white border border-sand-300 p-8 rounded-sm">
        <h3 className="font-display text-2xl text-stone-900 mb-2">Logo</h3>
        <p className="text-stone-600 text-sm mb-1">Wird im Header und Footer der Webseite angezeigt.</p>
        <p className="text-stone-600 text-xs mb-5">Formate: <strong>JPG, PNG, WEBP</strong> · Maximal 10 MB · Empfohlen: transparenter oder heller Hintergrund, mind. 400 px breit.</p>
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-40 h-40 border border-sand-300 rounded-sm bg-sand-100 flex items-center justify-center overflow-hidden">
            {info.logo_path ? (
              <img src={fileUrl(info.logo_path)} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
            ) : (
              <span className="text-xs text-stone-600 text-center px-3">Kein Logo hochgeladen</span>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <label className="inline-block">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 hover:bg-clay text-sand-100 rounded-sm cursor-pointer transition-colors">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</> : <><Upload className="w-4 h-4" /> Logo hochladen (JPG / PNG)</>}
              </span>
            </label>
            {info.logo_path && (
              <Button variant="outline" onClick={handleLogoRemove} data-testid="logo-remove-button" className="border-sand-300">
                <Trash2 className="w-4 h-4 mr-2" /> Logo entfernen
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* HERO IMAGE */}
      <div className="bg-white border border-sand-300 p-8 rounded-sm">
        <h3 className="font-display text-2xl text-stone-900 mb-2">Hero-Hintergrundbild</h3>
        <p className="text-stone-600 text-sm mb-1">Das große Foto ganz oben auf der Webseite (im Bold-Studio-Design als Vollbild-Hintergrund, im Editorial- und Werkstatt-Design als Hero-Bild rechts).</p>
        <p className="text-stone-600 text-xs mb-5">Formate: <strong>JPG, PNG, WEBP</strong> · Maximal 10 MB · Empfohlen: Querformat, mindestens 1600 px breit, hochwertige Aufnahme.</p>
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-64 aspect-[16/10] border border-sand-300 rounded-sm bg-sand-100 flex items-center justify-center overflow-hidden">
            {info.hero_image_path ? (
              <img src={fileUrl(info.hero_image_path)} alt="Hero" className="w-full h-full object-cover" data-testid="hero-preview" />
            ) : (
              <span className="text-xs text-stone-600 text-center px-3">Noch kein Bild gesetzt — es wird ein Standard-Stock-Foto angezeigt.</span>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <label className="inline-block">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handleHeroUpload}
                className="hidden"
                data-testid="hero-upload-input"
              />
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 hover:bg-clay text-sand-100 rounded-sm cursor-pointer transition-colors">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</> : <><Upload className="w-4 h-4" /> Hero-Bild hochladen (JPG / PNG)</>}
              </span>
            </label>
            {info.hero_image_path && (
              <Button variant="outline" onClick={handleHeroRemove} data-testid="hero-remove-button" className="border-sand-300">
                <Trash2 className="w-4 h-4 mr-2" /> Hero-Bild entfernen
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* DESIGN SELECTOR */}
      <div className="bg-white border border-sand-300 p-8 rounded-sm">
        <h3 className="font-display text-2xl text-stone-900 mb-2">Design der Webseite</h3>
        <p className="text-stone-600 text-sm mb-6">Wählen Sie einen visuellen Stil. Die öffentliche Webseite wird sofort umgestellt.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="design-selector">
          {DESIGNS.map((d) => {
            const active = info.design === d.key;
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => selectDesign(d.key)}
                data-testid={`design-option-${d.key}`}
                className={`text-left p-5 rounded-sm border-2 transition-all duration-200 ${
                  active
                    ? "border-clay bg-sand-100 ring-2 ring-clay/30"
                    : "border-sand-300 bg-white hover:border-stone-900"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium text-stone-900">{d.name}</p>
                  {active && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-clay text-sand-100 rounded-sm"><Check className="w-3 h-3" />Aktiv</span>}
                </div>

                {/* Mini preview tile */}
                <div className="h-20 rounded-sm overflow-hidden border border-sand-300 mb-4 flex" style={{ background: d.swatches[0] }}>
                  <div className="flex-1 flex items-center justify-center" style={{ color: d.swatches[3] }}>
                    <span className={`text-3xl ${d.fontPreview}`}>{d.fontSample}</span>
                  </div>
                  <div className="w-1/3 flex flex-col">
                    <div className="flex-1" style={{ background: d.swatches[1] }} />
                    <div className="flex-1" style={{ background: d.swatches[2] }} />
                  </div>
                </div>

                <div className="flex gap-1 mb-3" data-testid={`design-swatches-${d.key}`}>
                  {d.swatches.map((s, i) => (
                    <span key={i} className="w-6 h-6 rounded-sm border border-sand-300" style={{ background: s }} />
                  ))}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {d.layoutTags?.map((t) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 bg-sand-200 text-stone-700 rounded-sm uppercase tracking-wider font-medium">{t}</span>
                  ))}
                </div>

                <p className="text-xs text-stone-600 leading-relaxed">{d.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* SITE INFO FIELDS */}
      <form onSubmit={handleSave} className="bg-white border border-sand-300 p-8 rounded-sm space-y-5">
        <h3 className="font-display text-2xl text-stone-900 mb-2">Betriebsangaben</h3>
        <p className="text-stone-600 text-sm mb-4">Diese Angaben werden auf der öffentlichen Webseite angezeigt.</p>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="mb-2 block">{f.label}</Label>
            {f.type === "textarea" ? (
              <Textarea rows={5} value={info[f.key] ?? ""} onChange={update(f.key)} data-testid={`siteinfo-${f.key}`} />
            ) : (
              <Input type={f.type} value={info[f.key] ?? ""} onChange={update(f.key)} data-testid={`siteinfo-${f.key}`} />
            )}
          </div>
        ))}
        <Button type="submit" disabled={saving} data-testid="siteinfo-save" className="bg-stone-900 hover:bg-clay text-sand-100 rounded-sm">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Speichert…" : "Speichern"}
        </Button>
      </form>
    </div>
  );
}
